import { Alert, Badge, Button, Dropdown, Flex, Tag, Typography } from "antd";
import type { MenuProps } from "antd";
import {
  actionFor,
  canProvisionIdentity,
  canRequestPasswordReset,
  canRetryIdentity,
  canSynchronizeIdentity,
  IDENTITY_BADGE_STATUS,
  IDENTITY_STATUS_LABEL,
  needsVerifiedEmailWarning,
  type IdentityActionKind,
  type UserAccountAdminDetail,
} from "./userAccountAdminApi";
import { formatDateTimeVn } from "../../lib/dateFormat";
import { label } from "../../lib/labels";

export type AccountStatusAction =
  | Readonly<{ kind: "activate" }>
  | Readonly<{ kind: "lock" }>
  | Readonly<{ kind: "unlock" }>
  // S2.3.5 — identity delivery actions (Spec §13.1); union mở rộng additive.
  | Readonly<{ kind: "provision" }>
  | Readonly<{ kind: "retry" }>
  | Readonly<{ kind: "synchronize" }>
  | Readonly<{ kind: "request-password-reset" }>;

const ACCOUNT_STATUS_LABEL: Readonly<Record<UserAccountAdminDetail["status"], string>> = {
  INACTIVE: "Chưa kích hoạt",
  ACTIVE: "Đang hoạt động",
  LOCKED: "Đã khóa",
};

function statusTag(status: UserAccountAdminDetail["status"], displayName?: string | null) {
  const text = label(status, displayName ?? ACCOUNT_STATUS_LABEL[status]);
  if (status === "ACTIVE") return <Tag color="success">{text}</Tag>;
  if (status === "LOCKED") return <Tag color="error">{text}</Tag>;
  return <Tag>{text}</Tag>;
}

function lastLoginText(value: string | null | undefined): string {
  return value ? formatDateTimeVn(value) : "Chưa ghi nhận";
}

export function UserAccountDetailDrawer({
  detail,
  onAction,
  identityPending = null,
  onManageRoles,
}: {
  readonly detail: UserAccountAdminDetail;
  readonly onAction: (action: AccountStatusAction) => void;
  readonly identityPending?: IdentityActionKind | null;
  readonly onManageRoles?: () => void;
}) {
  // Action hiển thị được SUY RA từ trạng thái qua actionFor — nguồn sự thật duy nhất,
  // khớp với guard phía page (LIFECYCLE_ACTION_SOURCE). Backend vẫn là tầng enforcement.
  const action = actionFor(detail.status);

  // S2.3.5 — khối trạng thái đăng nhập (§12 DEFAULT trong Drawer). Payload không có
  // identity field (S2.3.4 thuần) → fail-closed: không render khối, không action.
  const identityStatus = detail.identityDeliveryStatus;
  const canProvision = canProvisionIdentity(detail);
  const canRetry = canRetryIdentity(detail);
  const canSynchronize = canSynchronizeIdentity(detail);
  const canReset = canRequestPasswordReset(detail);
  const showVerifiedEmailWarning = needsVerifiedEmailWarning(detail);

  // Sync + reset là PROGRESSIVE trong "Thao tác khác" (§12/§13.1); retry ở khối đăng nhập
  // vì §13.3 yêu cầu Alert riêng cho ACTION_REQUIRED và không có raw reason.
  const identityMenuItems: NonNullable<MenuProps["items"]> = [
    ...(canSynchronize ? [{ key: "synchronize", label: "Đồng bộ lại thông tin đăng nhập" }] : []),
    ...(canReset ? [{ key: "password-reset", label: "Gửi email đặt lại mật khẩu" }] : []),
  ];
  const showHigherRiskGroup = action === "lock" || identityMenuItems.length > 0;

  // Thứ tự nghiệp vụ: đăng nhập phải thiết lập XONG trước khi kích hoạt, nếu không
  // tài khoản đã kích hoạt mà vẫn không đăng nhập được. Chỉ nhắc khi còn chặng đăng
  // nhập dở; payload thiếu field identity (binary-compat S2.3.4) ⇒ không nhắc.
  const showLoginFirstHint = action === "activate" && identityStatus !== undefined && identityStatus !== "READY";

  return (
    <div className="account-detail">
      <section className="account-decision-strip" aria-label="Quyết định tài khoản">
        <Typography.Title level={4}>{detail.personDisplayName}</Typography.Title>
        <Flex gap={8} align="center" wrap>
          {statusTag(detail.status, (detail as Record<string, unknown>).statusDisplayName as string | undefined)}
          <div className="account-link-status">
            <Typography.Text type="secondary">Liên kết đăng nhập: </Typography.Text>
            <Typography.Text>{detail.identityLinked ? "Đã liên kết" : "Chưa liên kết"}</Typography.Text>
          </div>
        </Flex>
        <Flex gap={8} align="center" wrap>
          {action === "activate" && (
            <Button type="primary" onClick={() => onAction({ kind: "activate" })}>
              Kích hoạt
            </Button>
          )}
          {action === "unlock" && (
            <Button type="primary" onClick={() => onAction({ kind: "unlock" })}>
              Mở khóa
            </Button>
          )}
          {onManageRoles && (
            <Button onClick={onManageRoles}>
              Vai trò & phạm vi
            </Button>
          )}
        </Flex>
        {showLoginFirstHint && (
          <Typography.Text type="secondary">
            Thiết lập đăng nhập trước, rồi mới kích hoạt.
          </Typography.Text>
        )}
      </section>
      {identityStatus && (
        <section className="account-detail-section" aria-label="Trạng thái đăng nhập">
          <Typography.Title level={5}>Trạng thái đăng nhập</Typography.Title>
          {/* Signature S2.3.5: status rail nghiệp vụ "Tài khoản NCC → Đăng nhập".
              Badge dot + text — trạng thái không bao giờ chỉ truyền đạt bằng màu. */}
          <div className="account-identity-rail">
            <Typography.Text className="account-identity-node">Tài khoản NCC</Typography.Text>
            <span className="account-identity-arrow" aria-hidden="true">
              →
            </span>
            <Typography.Text className="account-identity-node">Đăng nhập</Typography.Text>
            <Badge
              status={IDENTITY_BADGE_STATUS[identityStatus]}
              text={label(identityStatus, (detail as Record<string, unknown>).identityDeliveryStatusDisplayName ?? IDENTITY_STATUS_LABEL[identityStatus])}
            />
          </div>
          {identityStatus === "ACTION_REQUIRED" && (
            // §13.3 Alert cho action-required; chỉ dùng nhãn nghiệp vụ đã duyệt, không raw reason.
            <Alert
              type="warning"
              showIcon
              title={IDENTITY_STATUS_LABEL.ACTION_REQUIRED}
              action={
                canRetry && (
                  <Button
                    size="small"
                    loading={identityPending === "retry"}
                    disabled={identityPending !== null && identityPending !== "retry"}
                    onClick={() => onAction({ kind: "retry" })}
                  >
                    Thử lại thiết lập đăng nhập
                  </Button>
                )
              }
            />
          )}
          {canProvision && (
            <Flex justify="flex-start">
              <Button
                loading={identityPending === "provision"}
                disabled={identityPending !== null && identityPending !== "provision"}
                onClick={() => onAction({ kind: "provision" })}
              >
                Thiết lập đăng nhập
              </Button>
            </Flex>
          )}
          {showVerifiedEmailWarning && (
            <Typography.Text type="warning">Chưa có email đã xác minh</Typography.Text>
          )}
        </section>
      )}
      <section className="account-detail-section" aria-label="Thông tin cần thiết">
        <Typography.Title level={5}>Thông tin cần thiết</Typography.Title>
        <div className="account-detail-row">
          <span className="account-detail-label">Tên đăng nhập</span>
          <span>{detail.username}</span>
        </div>
        <div className="account-detail-row">
          <span className="account-detail-label">Đơn vị công tác</span>
          <span>{detail.homeOrganization.name}</span>
        </div>
        <div className="account-detail-row">
          <span className="account-detail-label">Lần đăng nhập gần nhất</span>
          <span>{lastLoginText(detail.lastLoginAt)}</span>
        </div>
      </section>
      {showHigherRiskGroup && (
        <section className="account-detail-section account-higher-risk" aria-label="Thao tác khác">
          <Typography.Title level={5}>Thao tác khác</Typography.Title>
          <Flex gap={8} align="center" wrap>
            {action === "lock" && (
              <Button danger onClick={() => onAction({ kind: "lock" })}>
                Khóa tài khoản
              </Button>
            )}
            {identityMenuItems.length > 0 && (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: identityMenuItems,
                  onClick: ({ key }) =>
                    onAction(key === "password-reset" ? { kind: "request-password-reset" } : { kind: "synchronize" }),
                }}
              >
                <Button loading={identityPending === "synchronize"} disabled={identityPending !== null && identityPending !== "synchronize"}>
                  Xem thêm
                </Button>
              </Dropdown>
            )}
          </Flex>
        </section>
      )}
    </div>
  );
}
